import React, { useRef, useState, useCallback } from "react";
import Webcam from "react-webcam";
import { Button, Card, CardBody, Modal, ModalBody } from "reactstrap";
import { api } from "../../api/api";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { ComonIcons } from "../../contents/commonIcons";

const UploadOrCapture = ({
  label,
  onFileSelect,
  folder = "profile",
  setSelectedFile,
}) => {
  const fileInputRef = useRef(null);
  const webcamRef = useRef(null);

  const [takePicture, setTakePicture] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const [showSource, setShowSource] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  function base64ToFile(base64String, filename) {
    const arr = base64String.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  }

  async function uploadFileOrBase64(input, folder = "profile") {
    setLoading(true);
    let base64Data;
    let isFile = false;
    if (input instanceof File) {
      setImgSrc("");
      isFile = true;
      base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(input);
      });
      setSelectedFile(input);
    } else if (typeof input === "string" && input.startsWith("data:")) {
      base64Data = input;
      const capturedFile = base64ToFile(input, "captured.jpg");
      setSelectedFile(capturedFile);
    }

    const data = {
      fileName: input?.name || `captured_${Date.now()}`,
      source: base64Data,
      folder,
    };
    const response = await api("file", data, "post");
    isFile && setImgSrc(response?.data?.data);
    setLoading(false);
    onFileSelect && onFileSelect(response?.data?.data);
  }

  const capture = useCallback(async () => {
    setImgSrc("");
    const imageSrc = webcamRef.current.getScreenshot();
    await uploadFileOrBase64(imageSrc);
    setImgSrc(imageSrc);
    setTakePicture(false);
  }, [webcamRef]);

  return (
    <Card className="p-3 mb-3" id="no-border">
      <h5 className="mb-3" style={{ textAlign: "center" }}>
        {label || "Upload Member's Identity"}
      </h5>
      <div className="d-flex gap-2">
        {!takePicture ? (
          <Button
            color="primary"
            className="mr-4 upload-files-capture-btn"
            style={{ backgroundColor: "#999999" }}
            onClick={() => (takePicture ? capture() : setTakePicture(true))}
            disabled={loading}
          >
            📸 Take Picture
          </Button>
        ) : (
          <Button
            color="success"
            className="mr-4"
            onClick={capture}
            disabled={loading}
          >
            ✅ Capture
          </Button>
        )}

        <Button
          color="secondary"
          style={{ backgroundColor: "#3a3b3f" }}
          onClick={() => fileInputRef.current.click()}
          disabled={loading}
        >
          📂 Upload File
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={(e) => uploadFileOrBase64(e.target.files[0])}
        />
        {imgSrc && (
          <>
            <span className="mt-3 ml-4" onClick={() => setShowSource(true)}>
              {" "}
              {ComonIcons.eye}{" "}
            </span>
            <Modal
              isOpen={showSource}
              toggle={() => setShowSource(false)}
              centered
              size="lg"
              className="adding-popup"
            >
              <ModalBody
                className="d-flex justify-content-center align-items-center p-0"
                style={{ height: "80vh" }}
              >
                <div className="bg-white rounded w-100 h-100 text-center">
                  {/\.(jpg|jpeg|png|gif)$/i.test(imgSrc) ||
                  /^data:image\/(jpeg|png|gif)/i.test(imgSrc) ? (
                    // IMAGE PREVIEW
                    <TransformWrapper
                      initialScale={1}
                      minScale={0.5}
                      maxScale={4}
                      wheel={{ step: 0.2 }}
                      doubleClick={{ disabled: true }}
                    >
                      <TransformComponent
                        wrapperStyle={{ width: "100%", height: "100%" }}
                      >
                        <img
                          src={imgSrc}
                          alt="document"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </TransformComponent>
                    </TransformWrapper>
                  ) : /\.(doc|docx)$/i.test(imgSrc) ? (
                    // WORD DOC PREVIEW USING GOOGLE DOCS VIEWER
                    <iframe
                      src={`https://docs.google.com/gview?url=${encodeURIComponent(
                        imgSrc
                      )}&embedded=true`}
                      style={{ width: "100%", height: "100%" }}
                      frameBorder="0"
                      title="doc-preview"
                    />
                  ) : (
                    // FALLBACK (e.g. PDF or other formats)
                    <iframe
                      src={imgSrc}
                      style={{ width: "100%", height: "100%" }}
                      frameBorder="0"
                      title="file-preview"
                    />
                  )}
                </div>
              </ModalBody>
            </Modal>
          </>
        )}
      </div>

      {takePicture && (
        <div className="mt-3">
          <Webcam
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            videoConstraints={{ facingMode: "user" }}
            className="rounded border"
          />
        </div>
      )}
    </Card>
  );
};

export default UploadOrCapture;
